import FilterListIcon from '@material-ui/icons/FilterList';
import {AutoCompleteMultiInput, DropdownMultiSelect} from 'common';
import {ReferralPageAction} from 'placement/reducers/referralPage';
import Popper from 'popper.js';
import React, {FormEvent, useCallback} from 'react';
import {isFilterValueEmpty, NavigationFilterValue} from '../domains/NavigationFilterService';
import {PAGE, ServiceLocationEnumNameToValue} from "../const";
import DatePicker from "react-datepicker";
import {DatePickerInput} from "../../common/component/DatePickerInput";
import {subMonths, subYears} from "date-fns";


export type NavigationFilterOptions = {
    escalationLevels: Array<string>;
    referralConsiderations: Array<string>;
    scopeOfService: Array<string>;
    serviceType: Array<string>;
    serviceLocationTypes: Array<string>;
}

type Props = {
    page: PAGE;
    initFilterValue: NavigationFilterValue,
    filterOption: NavigationFilterOptions,
    dispatch: React.Dispatch<ReferralPageAction>
}

export function NavigationFilter({page, initFilterValue, dispatch, filterOption}: Props) {
    const [dropdownPopoverShow, setDropdownPopoverShow] = React.useState(false);
    const btnDropdownRef = React.useRef(null);
    const popoverDropdownRef = React.useRef(null);
    const nodeRef = React.useRef(null);
    const lastMonth = subMonths(new Date(), 1);

    const handleClickOutside = (e: Event) => {
        if ((nodeRef.current as any).contains(e.target)) {
            // inside
            return;
        }
        setDropdownPopoverShow(false);
    };

    // internal filter state
    const [filterValue, setFilterValue] = React.useState({...initFilterValue});

    const openDropdownPopover = () => {
        new Popper(btnDropdownRef.current!, popoverDropdownRef.current!, {
            placement: "bottom"
        });
        setDropdownPopoverShow(true);
    };

    const closeDropdownPopover = () => {
        setDropdownPopoverShow(false);
    };

    const onClick = useCallback(() => {
        dropdownPopoverShow
            ? closeDropdownPopover()
            : openDropdownPopover();
    }, [dropdownPopoverShow]);

    React.useEffect(() => {
        if (dropdownPopoverShow) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownPopoverShow]);

    // update internal value when init filter value updated
    React.useEffect(() => {
        setFilterValue(initFilterValue);
    }, [initFilterValue])


    const onSubmit = (e: FormEvent) => {
        e.preventDefault();
        // set filter state for the page
        dispatch({type: 'SET_FILTER', filter: filterValue});
        setDropdownPopoverShow(false);
    };

    const reset = () => {
        let defaultReferralSubmittedFrom: Date | null = subMonths(new Date(), 1);
        if (page === PAGE.OPEN_REFERRALS || page === PAGE.PROVIDER_REQUEST) {
            defaultReferralSubmittedFrom = null;
        }

        // set callback filter value
        setFilterValue({
            scopeOfService: '',
            preferredCity: '',
            escalationLevels: '',
            referralConsiderations: '',
            requiredLanguage: '',
            serviceType: '',
            provider: '',
            serviceLocationTypes: '',
            counties: '',
            referralSubmittedFrom: defaultReferralSubmittedFrom,
            referralSubmittedTo: null,
        });

        dispatch({
            type: 'SET_FILTER', filter: {
                scopeOfService: '',
                preferredCity: '',
                escalationLevels: '',
                referralConsiderations: '',
                requiredLanguage: '',
                serviceType: '',
                provider: '',
                serviceLocationTypes: '',
                counties: '',
                referralSubmittedFrom: defaultReferralSubmittedFrom,
                referralSubmittedTo: null,
            }
        });

        setDropdownPopoverShow(false);
    }

    return (<div ref={nodeRef} className='px-5 py'>
            <button
                className={'py-2 px-4 focus:bg-atlantis-200 hover:bg-atlantis-200 rounded ' +
                    ((dropdownPopoverShow || !isFilterValueEmpty(filterValue)) ? 'bg-atlantis-200' : '')}
                title="filter-button"
                onClick={onClick}
                ref={btnDropdownRef}>
                <FilterListIcon/>
            </button>
            <form onSubmit={onSubmit}>
                <div
                    ref={popoverDropdownRef}
                    className={
                        (dropdownPopoverShow ? "block " : "hidden ") +
                        "p-5 bg-white text-black text-base z-50 float-left py-2 list-none text-left rounded shadow-lg mt-1"
                    }
                    data-testid={'filters-container'}
                    style={{minWidth: "550px"}}
                >
                    <div className='text-lg font-medium'>
                        Filters
                    </div>
                    {[PAGE.MATCHED_REFERRALS, PAGE.COMPLETE_REFERRALS, PAGE.PROVIDER_REQUEST].includes(page) &&
                        <div className='pt-5 font-medium'>
                            <div className='pb-2'>
                                Provider
                            </div>
                            <AutoCompleteMultiInput field={'provider'}
                                                    fieldValue={filterValue.provider}
                                                    queryType='provider'
                                                    inputType='char-only'
                                                    setFieldValue={(val) => setFilterValue({
                                                        ...filterValue,
                                                        provider: val
                                                    })}/>
                        </div>}
                    <div className='pt-5 font-medium'>
                        <div className='pb-2'>
                            Scope of Services
                        </div>
                        <DropdownMultiSelect
                            field={'scopeOfService'}
                            options={filterOption.scopeOfService}
                            placeholder={'— — Choose Services — —'}
                            full
                            value={filterValue.scopeOfService}
                            setValue={(val) => setFilterValue({...filterValue, scopeOfService: val})}
                        />
                    </div>
                    <div className='pt-5 font-medium'>
                        <div className='pb-2'>
                            Service Types
                        </div>
                        <DropdownMultiSelect
                            field={'serviceType'}
                            options={filterOption.serviceType}
                            placeholder={'— — Choose Service Types — — '}
                            full
                            value={filterValue.serviceType}
                            setValue={(val) => setFilterValue({...filterValue, serviceType: val})}
                        />
                    </div>
                    <div className='pt-5 font-medium'>
                        <div className='pb-2'>
                            Service Location
                        </div>
                        <DropdownMultiSelect
                            field={'serviceLocation'}
                            options={filterOption.serviceLocationTypes}
                            labelMapperFunc={(name) => ServiceLocationEnumNameToValue.get(name) ?? ""}
                            placeholder={'— — Choose Service Location — — '}
                            full
                            value={filterValue.serviceLocationTypes}
                            setValue={(val) => setFilterValue({...filterValue, serviceLocationTypes: val})}
                        />
                    </div>
                    <div className='pt-5 font-medium'>
                        <div className='pb-2'>
                            Escalation Levels
                        </div>
                        <DropdownMultiSelect
                            field={'escalationLevels'}
                            options={filterOption.escalationLevels}
                            placeholder={'— — Choose Levels — —'}
                            full
                            value={filterValue.escalationLevels}
                            setValue={(val) => setFilterValue({...filterValue, escalationLevels: val})}
                        />
                    </div>
                    <div className='pt-5 font-medium'>
                        <div className='pb-2'>
                            Referral Considerations
                        </div>
                        <DropdownMultiSelect
                            field={'referralConsiderations'}
                            options={filterOption.referralConsiderations}
                            placeholder={'— — Choose Considerations — —'}
                            full
                            value={filterValue.referralConsiderations}
                            setValue={(val) => setFilterValue({...filterValue, referralConsiderations: val})}
                        />
                    </div>
                    <div className='pt-5 font-medium'>
                        <div className='pb-2'>
                            County
                        </div>
                        <AutoCompleteMultiInput field={'counties'}
                                                fieldValue={filterValue.counties}
                                                queryType='county'
                                                inputType='char-only'
                                                setFieldValue={(val) => setFilterValue({
                                                    ...filterValue,
                                                    counties: val
                                                })}/>
                    </div>
                    <div className='pt-5 font-medium'>
                        <div className='pb-2'>
                            Preferred Service City
                        </div>
                        <AutoCompleteMultiInput field={'preferredCity'}
                                                fieldValue={filterValue.preferredCity}
                                                queryType='city'
                                                inputType='char-only'
                                                setFieldValue={(val) => setFilterValue({
                                                    ...filterValue,
                                                    preferredCity: val
                                                })}/>
                    </div>
                    <div className='pt-5 font-medium'>
                        <div className='pb-2'>
                            Required Language
                        </div>
                        <AutoCompleteMultiInput field={'requiredLanguage'}
                                                fieldValue={filterValue.requiredLanguage}
                                                queryType='language'
                                                inputType='char-only'
                                                setFieldValue={(val) => setFilterValue({
                                                    ...filterValue,
                                                    requiredLanguage: val
                                                })}/>
                    </div>
                    {
                        page !== PAGE.PROVIDER_REQUEST &&
                        <>
                            <div className='pt-5 font-medium'>
                                <div className='pb-2'>
                                    Referral Submitted From
                                </div>
                                <DatePicker
                                    wrapperClassName="w-full"
                                    minDate={subYears(new Date(), 1)}
                                    selected={filterValue.referralSubmittedFrom || null}
                                    onChange={(newValue: Date | null) => {
                                        if (newValue == null && page !== PAGE.OPEN_REFERRALS) {
                                            newValue = lastMonth;
                                        }
                                        setFilterValue({
                                            ...filterValue,
                                            referralSubmittedFrom: newValue
                                        })
                                    }
                                    }
                                    customInput={<DatePickerInput
                                        fieldName="referralSubmittedFrom"
                                        value={undefined}
                                    />}
                                />
                            </div>
                            <div className='pt-5 font-medium'>
                                <div className='pb-2'>
                                    Referral Submitted To
                                </div>
                                <DatePicker
                                    wrapperClassName="w-full"
                                    selected={filterValue.referralSubmittedTo || null}
                                    onChange={(newValue: Date | null) => setFilterValue({
                                        ...filterValue,
                                        referralSubmittedTo: newValue
                                    })}
                                    customInput={<DatePickerInput
                                        fieldName="referralSubmittedTo"
                                        value={undefined}
                                    />}
                                />
                            </div>
                        </>
                    }
                    <div className='pt-10 pb-5 flex flex-row-reverse'>
                        <div className='pl-5'>
                            <button type='submit'
                                    className='bg-atlantis-200 font-light hover:bg-atlantis-300 text-white py-2 px-4 rounded'
                                    style={{minWidth: '82px'}}>
                                Apply
                            </button>
                        </div>

                        <button type='button' onClick={() => reset()}
                                className='bg-gray-300 font-light hover:bg-gray-500 text-black py-2 px-4 rounded'
                                style={{minWidth: '82px'}}>
                            Reset
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
